/**
 * The tables Teslyn Field synchronises with Teslyn Cloud (docs/specs/sync/schema-parity.md, ADR-018).
 *
 * `columns` are the columns of the Field SQLite table (crates/teslyn-db/src/migrations), in schema order. Every one
 * has a Postgres mirror of the same name; Postgres adds `tenant_id` on project-scoped tables and a few cloud-only
 * columns (for example `project.client_org_id`), all nullable or defaulted so a Field insert succeeds.
 * supabase/tests/sync-parity.test.ts checks this list against both databases, so a new column or table cannot be
 * forgotten: add it here, to the Postgres mirror, and to the SQLite registry `sync_table`.
 *
 * The order is the dependency order: parents before children, so uploads and downloads never break a foreign key.
 */

/** How a table's rows are scoped: the project row itself, the tenant (shared by its projects), or one project. */
export type SyncScope = 'root' | 'tenant' | 'project';

export interface SyncedTable {
  readonly name: string;
  readonly scope: SyncScope;
  readonly columns: readonly string[];
}

const audit = ['created_at', 'updated_at', 'updated_by', 'deleted_at'] as const;

/** The columns of `signoff_request` a laptop keeps (none of them is ever uploaded). */
const SIGNOFF_REQUEST_COLUMNS = [
  'id',
  'project_id',
  'document_kind',
  'document_id',
  'document_revision',
  'title',
  'state',
  'signers',
  'message',
  'sent_at',
  'completed_at',
  'signed_available',
  'created_by_name',
  ...audit,
] as const;

/** The columns of `geosoft_template_file` a laptop keeps (decision 889; none of them is ever uploaded). */
const GEOSOFT_TEMPLATE_FILE_COLUMNS = [
  'id',
  'tenant_id',
  'family',
  'rel_path',
  'size_bytes',
  'sha256',
  'object_path',
  'uploaded_by_name',
  ...audit,
] as const;

/** The columns of `final_report_text` a laptop keeps (none of them is ever uploaded). */
const FINAL_REPORT_TEXT_COLUMNS = [
  'id',
  'tenant_id',
  'project_id',
  'sections',
  'equipment',
  'changed_by_name',
  ...audit,
] as const;

/** The columns of `safety_brief` (0074). */
const SAFETY_BRIEF_COLUMNS = [
  'id',
  'project_id',
  'kind',
  'brief_date',
  'opened_at',
  'location',
  'leader_person_id',
  'leader_name',
  'attendees',
  'snapshot',
  'hazards',
  'checks',
  'conditions',
  'items',
  'notes',
  'next_meeting',
  'finished_at',
  'content_changed_at',
  'pdf_name',
  'pdf_sha256',
  'pdf_revision',
  'pdf_made_at',
  'recorded_by_name',
  ...audit,
] as const;

/** The columns of `safety_brief_sign` (0074; a laptop's triggers refuse local writes). */
const SAFETY_BRIEF_SIGN_COLUMNS = [
  'id',
  'project_id',
  'brief_id',
  'person_id',
  'name',
  'role',
  'checks',
  'reconfirm',
  'on_leader_phone',
  'signed_at',
  ...audit,
] as const;

/** The columns of `safety_action` (0074; a laptop's triggers refuse local writes). */
const SAFETY_ACTION_COLUMNS = [
  'id',
  'project_id',
  'brief_id',
  'text',
  'owner_person_id',
  'owner_name',
  'all_crew',
  'due_date',
  'done_at',
  'done_brief_id',
  'done_by_name',
  ...audit,
] as const;

export const SYNCED_TABLES: readonly SyncedTable[] = [
  {
    name: 'project',
    scope: 'root',
    columns: [
      'id',
      'tenant_id',
      'number',
      'title',
      'client_name',
      'area',
      'state',
      'job_type',
      'operating_base',
      'basemag_name',
      'folder_path',
      'cover_image_path',
      'epsg',
      'status',
      'legacy_json',
      ...audit,
      'fuel_drum_l',
      'mr_warn_h',
      'qc_hand_sample_pct',
      'qc_test_line_pct',
      'qc_overrides',
      'qc_rules',
      'qc_family',
      'qc_hard_deck_m',
      'qc_lower_working_m',
      'qc_min_satellites',
      'qc_noise_channels',
      'naips_aerodromes',
      'windy_link',
      'stand_down_from',
      'stand_down_to',
      'diurnal_plot1_y_scale_nt',
      'diurnal_gradient_y_scale_nt',
      'windy_boundary_hash',
      'qc_crystal_ground_cal_pct',
      'qc_crystal_fwhm_pts',
      'qc_crystal_test_line_pct',
    ],
  },
  {
    name: 'aircraft',
    scope: 'tenant',
    columns: [
      'id',
      'tenant_id',
      'registration',
      'type_name',
      'fuel_flow_lph',
      'mr_interval_h',
      'ttis_at_issue_h',
      'ttis_at_start_h',
      'photo_path',
      'legacy_json',
      ...audit,
      'fuel_type',
      'max_flight_h',
      'fuel_capacity_l',
      // the aircraft register settings (decision 506, 0032)
      'engines',
      'engine_starts_tracked',
      'oil_uplift_tracked',
      'fuel_unit',
      'airframe',
      // its own photo set in Teslyn Cloud (decision 598, 0043): never changed on a laptop
      'cloud_photo_path',
      // the JSA's empty weight, MTOW and reserve kept in the register (decision 863, 0060)
      'empty_weight_kg',
      'mtow_kg',
      'reserve_min',
      // the altimeter and barometer calibrations set in Teslyn Cloud (decision 147, 0067): never changed on a laptop
      'altimeter_cals',
      // the oil dipstick's unit, a register setting (R150, decision 920; 0071)
      'oil_dipstick_unit',
      // "Uses V2Track flight watch", a register setting (R184, decision 997; 0077)
      'uses_flight_watch',
    ],
  },
  // the staff register (decision 422, 0027); email and phone reach the staff's laptops (N11, decision 473; SQLite
  // 0058), never a client's (clients hold no membership, so the stream gives them nothing); the sign-in link stays
  // in Teslyn Cloud
  {
    name: 'person',
    scope: 'tenant',
    columns: [
      'id',
      'tenant_id',
      'name',
      'role',
      'rank',
      ...audit,
      'roles',
      'title',
      'active',
      'email',
      'phone',
      // N11 (a), decision 471: added on site by field crew (SQLite 0058, Postgres 20261028120400)
      'added_on_site',
    ],
  },
  {
    name: 'position',
    scope: 'project',
    // a test line's end (R14, 0032)
    columns: [
      'id',
      'project_id',
      'kind',
      'name',
      'easting',
      'northing',
      ...audit,
      'easting_end',
      'northing_end',
    ],
  },
  {
    name: 'block',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'name',
      'key',
      'digit',
      'kind',
      'epsg',
      'terrain_clearance_m',
      'trav_spacing_m',
      'trav_direction_deg',
      'tie_spacing_m',
      'tie_direction_deg',
      'planned_km',
      'boundary',
      ...audit,
      // N32 (SQLite 0058, Postgres 20261028120000)
      'report_include',
      'km_completed',
    ],
  },
  {
    name: 'planned_line',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'block_id',
      'number',
      'kind',
      'x1',
      'y1',
      'x2',
      'y2',
      'length_m',
      'reflight_of_line_id',
      ...audit,
      'segment_from_m',
      'segment_to_m',
      // R245: the import that brought the line into the plan (0081, Postgres 20261105160000)
      'lines_file_id',
      // decision 1051: the planned patch or reflight an imported line took the place of
      'replaces_line_id',
    ],
  },
  {
    name: 'flight',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'aircraft_id',
      'jday',
      'date',
      'flt_num',
      'log_path',
      'folders',
      'rego',
      'test_km',
      'comp_box',
      'comp_lines',
      'imported_at',
      'import_warnings',
      ...audit,
      'takeoff_local',
      'landing_local',
    ],
  },
  {
    name: 'line_status',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'flight_id',
      'planned_line_id',
      'line_number',
      'flown_m',
      'proposed_m',
      'accepted_m',
      'acceptance',
      'use_km',
      'reason',
      'note',
      'reviewed_by',
      'reviewed_at',
      'reflight_of_status_id',
      ...audit,
      'direction_deg',
      'start_along_m',
      'end_along_m',
      'log_completion',
      'xyz_check',
    ],
  },
  {
    name: 'line_status_history',
    scope: 'project',
    columns: [
      'id',
      'line_status_id',
      'changed_at',
      'changed_by',
      'from_acceptance',
      'to_acceptance',
      'from_accepted_m',
      'to_accepted_m',
      'reason',
      'note',
      ...audit,
      // review, deleted or restored (R36, 0032)
      'event',
    ],
  },
  {
    name: 'daily_entry',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'date',
      'jday',
      'aircraft_id',
      'base_mag_position_id',
      'ground_cal_position_id',
      'test_line_position_id',
      'pilot_id',
      'operator1_id',
      'operator2_id',
      'entry_type',
      'standby_type',
      'flt_num',
      'takeoff_time',
      'blocks',
      'default_block_id',
      'days_alloc',
      'svy_hours',
      'cbx_hours',
      'cal_hours',
      'fry_hours',
      'ext_hours',
      'fuel_used_l',
      'fuel_source',
      'fuel_location',
      'fuel_load_l',
      'starts',
      'oil_l',
      'oil_r',
      'new_mr_ttis_h',
      'op_comment',
      'qc_comment',
      'mobilization',
      'log_file',
      ...audit,
      'pilot2_id',
      // the names kept when a crew member is removed for good from the register (decision 602, 0045)
      'pilot_name',
      'pilot2_name',
      'operator1_name',
      'operator2_name',
      // a removed pilot's rank kept with the name (decision 662, 0046)
      'pilot_rank',
      'pilot2_rank',
      // a flight started on Teslyn Mobile (batch 7, migration 20261028160000, Field SQLite 0066)
      'planned_flight_h',
      'flight_time_h',
      'entry_origin',
      // the oil level on the dipstick before oil is added, per engine (R150, decision 920; 0071)
      'oil_dipstick_l',
      'oil_dipstick_r',
    ],
  },
  {
    name: 'cal_result',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'flight_id',
      'daily_entry_id',
      'kind',
      'used',
      'tc',
      'k',
      'u',
      'th',
      'easting',
      'northing',
      'altitude_m',
      'th_peak',
      'fwhm_pct',
      'values_json',
      'review_state',
      'review_reason',
      'reviewed_by',
      'reviewed_at',
      'recorded_at',
      'source_log',
      'source_line',
      'zdas_value',
      'zdas_reference',
      'zdas_pct',
      'zdas_pass',
      ...audit,
    ],
  },
  {
    name: 'cal_result_history',
    scope: 'project',
    columns: [
      'id',
      'cal_result_id',
      'changed_at',
      'changed_by',
      'from_state',
      'to_state',
      'from_flight_id',
      'to_flight_id',
      'from_used',
      'to_used',
      'reason',
      ...audit,
    ],
  },
  {
    name: 'fuel_ledger_entry',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'date',
      'time',
      'kind',
      'location',
      'truck_avgas_l',
      'truck_turbine_l',
      'drums_avgas',
      'drums_turbine',
      ...audit,
      // decision 159 (SQLite 0014, Postgres 20261019210000): added after the audit columns
      'uplift_truck_avgas_l',
      'uplift_truck_turbine_l',
      'uplift_drums_avgas',
      'uplift_drums_turbine',
      // decision 193: the delivery docket's path in the project folder
      'docket_path',
    ],
  },
  {
    name: 'maintenance_item',
    scope: 'project',
    columns: ['id', 'project_id', 'aircraft_id', 'date', 'text', 'fixed', ...audit],
  },
  {
    name: 'report',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'kind',
      'period',
      'revision',
      'data_revision',
      'manifest_json',
      'pdf_path',
      'generated_by',
      'generated_at',
      ...audit,
    ],
  },
  {
    name: 'qc_run',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'flight_id',
      'kind',
      'family',
      'label',
      'source_path',
      'source_bytes',
      'source_modified_at',
      'geosoft_version',
      'driver_version',
      'spec',
      'status',
      'error',
      'started_at',
      'finished_at',
      'seconds',
      'line_count',
      'fail_count',
      'warn_count',
      'pass_count',
      'notes',
      ...audit,
    ],
  },
  {
    name: 'qc_line_result',
    scope: 'project',
    columns: [
      'id',
      'run_id',
      'project_id',
      'line_name',
      'line_number',
      'line_status_id',
      'verdict',
      'rows',
      'fid_start',
      'fid_end',
      'length_m',
      'tests',
      ...audit,
    ],
  },
  // aircraft tracking (crates/teslyn-db 0011, supabase 20261019110100; docs/specs/tracker.md §6)
  {
    name: 'tracker_asset',
    scope: 'tenant',
    columns: [
      'id',
      'tenant_id',
      'provider',
      'account_label',
      'device_id',
      'label',
      'registration',
      'icao_hex',
      'kind',
      'colour',
      'fleet_group',
      'aircraft_id',
      'hidden',
      ...audit,
    ],
  },
  {
    name: 'tracking_settings',
    scope: 'tenant',
    columns: [
      'id',
      'tenant_id',
      'lost_tracking_minutes',
      'provider_minutes',
      'clients_see_public_traffic',
      'source_priority',
      ...audit,
      // the minutes before the two V2Track flight watch alerts (R184; 0077)
      'flight_watch_start_minutes',
      'flight_watch_stop_minutes',
    ],
  },
  {
    name: 'rfds_identity',
    scope: 'tenant',
    columns: ['id', 'tenant_id', 'icao_hex', 'registration', 'callsign', 'source', 'confirmed_at', ...audit],
  },
  {
    name: 'tracker_assignment',
    scope: 'project',
    columns: ['id', 'project_id', 'asset_id', 'starts_at', 'ends_at', ...audit],
  },
  // saved flight plan versions (MB decision 85) and the crew's flown marks (item 04-031); docs/specs/planning.md §3
  {
    name: 'flight_plan',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'version',
      'based_on_id',
      'kind',
      'name',
      'note',
      'saved_by_name',
      'sources',
      'request',
      'result',
      'flights',
      'line_km',
      'flight_hrs',
      'productivity_pct',
      ...audit,
    ],
  },
  {
    name: 'line_flown_override',
    scope: 'project',
    columns: ['id', 'project_id', 'flown_key', 'line_number', 'flown', ...audit],
  },
  // observed traffic: Oz Runways and AvPlan aircraft pinned on the project and their notes (decision 70; 03-041 to
  // 03-043; crates/teslyn-db 0024, supabase 20261021010000; docs/specs/tracker.md §5.5)
  {
    name: 'observed_aircraft',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'callsign',
      'source',
      'folder',
      'how',
      'area',
      'pinned_at',
      'pinned_by_name',
      ...audit,
    ],
  },
  {
    name: 'observed_aircraft_note',
    scope: 'project',
    columns: ['id', 'project_id', 'callsign', 'body', 'author_name', ...audit],
  },
  // who is on the project (decision 422, 0027), set in Teslyn Cloud by managers and office staff
  {
    name: 'project_crew',
    scope: 'project',
    // person_name: the name kept when the person is removed for good (decision 602, 0045); person_id is then null
    columns: [
      'id',
      'project_id',
      'person_id',
      'role',
      'start_date',
      'end_date',
      'note',
      ...audit,
      'person_name',
    ],
  },
  // project photos (decisions 427-431; crates/teslyn-db 0028, supabase 20261021040000; docs/specs/cloud/project-photos.md).
  // Teslyn Cloud adds tenant_id, added_by, shared_at, shared_by and share_requested_by, which never sync to a laptop.
  {
    name: 'project_photo',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'file_name',
      'file_ext',
      'photo_path',
      'size_bytes',
      'sha256',
      'width',
      'height',
      'caption',
      'category',
      'taken_local',
      'taken_offset',
      'taken_lat',
      'taken_lon',
      'added_by_name',
      'source',
      ...audit,
      // decisions 509 and 515 (0038): put forward for sharing; the state Teslyn Cloud keeps (shared_at stays cloud-only)
      'share_requested_at',
      'share_requested_by_name',
      'share_state',
      // decisions 665 and 712 (0048): the project's weekly report cover, chosen in Teslyn Cloud only (CLOUD_ONLY_WRITES)
      'weekly_cover',
    ],
  },
  // The lines files Teslyn wrote for the nav system (R23, R24, F16; 0038, Postgres 20261025000500). Teslyn Cloud adds
  // tenant_id and created_by.
  {
    name: 'lines_file',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'block_id',
      'file_name',
      'folder',
      'purpose',
      'lines',
      'line_count',
      'length_m',
      'sha256',
      'note',
      'created_by_name',
      ...audit,
      // R245: what was imported and its untouched copy (0081, Postgres 20261105160000)
      'import_type',
      'import_copy',
    ],
  },
  // acknowledged calibration warnings (MB's R61, 25 Oct 2026; crates/teslyn-db 0040, supabase 20261025000900).
  // Teslyn Cloud adds tenant_id and acknowledged_by, which never sync to a laptop.
  {
    name: 'cal_warning_ack',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'kind',
      'warning_key',
      'warning_date',
      'message',
      'acknowledged_by_name',
      'acknowledged_at',
      ...audit,
    ],
  },
  // Acknowledge & accepted in Geosoft QC (R58, decision 538; crates/teslyn-db 0041, supabase 20261025001000):
  // append-only, the newest row of a QC line result is its state. Teslyn Cloud adds tenant_id and by_user.
  {
    name: 'qc_acceptance',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'qc_line_result_id',
      'line_status_id',
      'action',
      'verdict',
      'reason',
      'by_name',
      'acted_at',
      'from_acceptance',
      'from_accepted_m',
      'from_reason',
      ...audit,
    ],
  },
  // The refly list's Checked and Added to aircraft ticks (decision 544; 0042, Postgres 20261025001200): append-only,
  // the newest row of a row and tick is its state. Teslyn Cloud adds tenant_id and by_user, and stamps by_name.
  {
    name: 'refly_tick',
    scope: 'project',
    columns: ['id', 'project_id', 'row_key', 'line_status_id', 'tick', 'ticked', 'by_name', ...audit],
  },
  // Diurnal QC's kept bad sections of a flown line (decision 563; 0042, Postgres 20261025001200): the newest live row
  // of a line status counts. Teslyn Cloud adds tenant_id.
  {
    name: 'line_bad_section',
    scope: 'project',
    columns: ['id', 'project_id', 'line_status_id', 'source', 'sections', ...audit],
  },
  // Custom hazard pins, shared records of the project (Diurnal parity 05-022, decision 620; 0049, Postgres
  // 20261027005000): placed, dragged, edited and removed on any laptop. Teslyn Cloud adds tenant_id and placed_by,
  // and writes placed_by_name from the staff register.
  {
    name: 'hazard_pin',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'hazard_map',
      'name',
      'description',
      'lat',
      'lng',
      'icon',
      'colour',
      'placed_by_name',
      ...audit,
    ],
  },
  // Landholder parcels, shared records of the project (Diurnal parity B4, decision 618; 0050, Postgres
  // 20261027006000): pinned, imported, combined, edited and removed on any laptop. Teslyn Cloud adds tenant_id and
  // created_by.
  {
    name: 'land_parcel',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'name',
      'source',
      'folder',
      'geometry',
      'notes',
      'needs_notification',
      'objection',
      'contacts',
      'attributes',
      ...audit,
    ],
  },
  // The processor's refly flags (MB's R127; 0051, Postgres 20261027100000): a flown line's section flagged by its zFids,
  // removed only by soft delete. Teslyn Cloud adds tenant_id and created_by, and stamps by_name.
  {
    name: 'refly_flag',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'line_status_id',
      'zfid_start',
      'zfid_end',
      'from_m',
      'to_m',
      'test_key',
      'reason',
      'by_name',
      ...audit,
    ],
  },
  // JSP safety meetings, shared records of the project (R119; 0053, Postgres 20261027130000): recorded, changed and
  // removed on any laptop. Teslyn Cloud adds tenant_id and recorded_by, and writes recorded_by_name.
  {
    name: 'jsp_meeting',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'meeting_date',
      'location',
      'recce',
      'attendees',
      'risks',
      'notes',
      'map_picture_at',
      'pdf_name',
      'pdf_sha256',
      'pdf_revision',
      'pdf_made_at',
      'recorded_by_name',
      ...audit,
    ],
  },
  // DocuSeal sign-off requests (R120; 0053, Postgres 20261027130000): written only by Teslyn Cloud's docuseal function
  // and downloaded read only (every column is CLOUD_ONLY_WRITES; the laptop's triggers refuse local writes). Teslyn
  // Cloud keeps tenant_id, the DocuSeal submission, the signers' emails and the storage paths to itself.
  {
    name: 'signoff_request',
    scope: 'project',
    columns: SIGNOFF_REQUEST_COLUMNS,
  },
  // The JSA (R13, decision 837; 0056, Postgres 20261027180000): one per project, written on any laptop. Teslyn Cloud
  // adds tenant_id and recorded_by, and writes recorded_by_name.
  {
    name: 'jsa',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'part_a_person_id',
      'part_a_name',
      'part_a_date',
      'part_b_person_id',
      'part_b_name',
      'part_b_date',
      'details',
      'operating',
      'blocks',
      'aircraft',
      'logistics',
      'airspace',
      'recon',
      'additional_risks',
      'sign_date',
      'crew',
      'managers',
      'pictures',
      'pdf_name',
      'pdf_sha256',
      'pdf_revision',
      'pdf_made_at',
      'recorded_by_name',
      ...audit,
    ],
  },
  // The safety register (decision 837; 0056, Postgres 20261027180000): accidents, incidents, near misses and
  // landholder complaints, recorded on any laptop. Teslyn Cloud adds tenant_id and recorded_by.
  {
    name: 'safety_entry',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'kind',
      'occurred_on',
      'occurred_at',
      'topic',
      'reported_by_person_id',
      'reported_by_name',
      'reported_by_role',
      'people',
      'aircraft_id',
      'aircraft_registration',
      'location',
      'lat',
      'lon',
      'land_parcel_id',
      'landholder_name',
      'landholder_details',
      'landholder_phone',
      'description',
      'actions',
      'corrective_action_taken',
      'follow_up',
      'follow_up_due',
      'closed_at',
      'closed_by_name',
      'recorded_by_name',
      ...audit,
      // the project photos linked to the entry (decision 863, 0060)
      'photo_ids',
    ],
  },
  // Documents read from a photo or scan (F03, F09; 0063, Postgres 20261028170000): MR pages, fuel dockets and
  // receipts with the values a person checked. Teslyn Cloud adds tenant_id and added_by, and writes added_by_name.
  {
    name: 'document_scan',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'kind',
      'aircraft_id',
      'fuel_entry_id',
      'daily_entry_id',
      'file_name',
      'file_ext',
      'file_path',
      'size_bytes',
      'sha256',
      'checked_values',
      'read_values',
      'read_by',
      'read_at',
      'checked_by_name',
      'checked_at',
      'added_by_name',
      'source',
      ...audit,
    ],
  },
  // the template Geosoft folder per aircraft type kept in Teslyn Cloud (decision 889, SQLite 0069): the manifest,
  // written by Teslyn Cloud only (GEOSOFT_TEMPLATE_FILE_COLUMNS are all cloud-only writes); the files come from Storage
  { name: 'geosoft_template_file', scope: 'tenant', columns: GEOSOFT_TEMPLATE_FILE_COLUMNS },
  // The final report's standard text and equipment list (decision 885; 0070, Postgres 20261030120000): written in
  // Teslyn Cloud for the company (no project) and per project, downloaded read only to every laptop of the
  // organisation (every column is CLOUD_ONLY_WRITES; the laptop's triggers refuse local writes).
  {
    name: 'final_report_text',
    scope: 'tenant',
    columns: FINAL_REPORT_TEXT_COLUMNS,
  },
  // Shape file layers, shared records of the project (MB's R152, decision 921; 0072, Postgres 20261102180000):
  // hazards or another layer from a shape file, added, renamed and removed on any laptop. Teslyn Cloud adds
  // tenant_id and created_by.
  {
    name: 'shape_layer',
    scope: 'project',
    columns: ['id', 'project_id', 'name', 'kind', 'source', 'crs', 'features', ...audit],
  },
  // The pre-flight brief and safety meeting (R161, decisions 916, 917, 974; 0074, Postgres 20261103110000): made on
  // Teslyn Mobile; a laptop changes only the PDF columns (its triggers refuse the rest; not CLOUD_ONLY_WRITES, which
  // the phone's outbox reads too).
  // Teslyn Cloud adds tenant_id and recorded_by, and writes recorded_by_name.
  { name: 'safety_brief', scope: 'project', columns: SAFETY_BRIEF_COLUMNS },
  // Each person's "Read and understood" (decision 974.3), made on the phones, read only on a laptop. Teslyn Cloud adds
  // tenant_id and signed_by.
  { name: 'safety_brief_sign', scope: 'project', columns: SAFETY_BRIEF_SIGN_COLUMNS },
  // The actions shared by every brief and meeting (decision 974.2), made and ticked on the phones, read only here.
  { name: 'safety_action', scope: 'project', columns: SAFETY_ACTION_COLUMNS },
  // The project's crew check list and the client switch (decision 974.5, 974.6), changed in Project settings on a
  // laptop or in Teslyn Cloud. One row per project, its id the project's.
  {
    name: 'brief_setting',
    scope: 'project',
    columns: ['id', 'project_id', 'checks', 'client_visible', 'changed_by_name', ...audit],
  },
  // The Height breach report's entries (R196, R197, decision 1017; 0075 and 0076, Postgres 20261104000000): added,
  // their reason changed and removed on any laptop; staff of the project read them in Teslyn Cloud, clients never.
  // Teslyn Cloud adds tenant_id and created_by. The chart goes through the upload queue (breach_chart). The audit
  // columns are last because 0076 added updated_by after the table's own columns; the rest are 0075's order.
  {
    name: 'height_breach',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'run_id',
      'result_id',
      'flight_id',
      'test_key',
      'line_name',
      'fid_start',
      'fid_end',
      'facts',
      'reason',
      'picture_sha256',
      'history',
      'created_at',
      'created_by_name',
      'updated_at',
      'updated_by_name',
      'deleted_at',
      'deleted_reason',
      'deleted_by_name',
      'updated_by',
    ],
  },
  // A hazard marked not present on the recce (MB's R243; 0080, Postgres 20261105140000): pressed on the phones, one row
  // per hazard, read by the laptops. Teslyn Cloud adds tenant_id and marked_by, and writes marked_by_name from the
  // staff register.
  {
    name: 'hazard_mark',
    scope: 'project',
    columns: [
      'id',
      'project_id',
      'hazard_key',
      'source',
      'name',
      'lat',
      'lng',
      'state',
      'marked_at',
      'marked_by_name',
      ...audit,
    ],
  },
];

/**
 * Field tables that stay on the laptop (docs/specs/sync/schema-parity.md). The basemag readings are packed columns
 * of up to millions of samples; each laptop imports basemag and _Diurnal.XYZ files from the project folder. The cloud
 * basemag session (phase 5) brings them to Postgres and Storage together.
 */
export const FIELD_ONLY_TABLES: readonly string[] = [
  // where each project's hazard pack stands on this laptop (0079); Teslyn Cloud's record is public.hazard_pack
  'hazard_pack_sync',
  'basemag_series',
  'basemag_readings',
  'diurnal_line',
  // trail points of the last 48 hours on this laptop (Q48); the cloud keeps its own tracker_position
  'tracker_point',
  // aircraft details answered by the cloud, kept 30 days (24 hours when not found) for offline reading (0021)
  'aircraft_profile',
  // the queue of files this laptop uploads (0015, docs/specs/cloud/storage-uploads.md); the cloud keeps
  // report_publication and project_file
  'file_upload',
  // the queue of files this laptop archives (0021, docs/specs/cloud/file-archive.md); the cloud keeps archive_file
  'archive_upload',
  // every fix this laptop saw of a pinned aircraft, kept for the life of the project (0024, decision 70)
  'observed_point',
  // lost-tracking, SOS and resumed alerts queued for the phone relay of phase 5 (0024, 03-037)
  'tracking_alert',
  // the superuser data tools' backup and edit log (0025, docs/specs/basemag-edits.md), MB's laptop only (decision 49)
  'basemag_edit_backup',
  'basemag_edit',
  // the sign-off queue of this laptop (0053, R120): the PDFs waiting to be sent to Teslyn Cloud's docuseal function
  'signoff_outbox',
  // the processors' zips this laptop makes and sends after a daily report is published (0073, R165, decision 925); the
  // cloud keeps public.deliverable
  'processor_zip',
];

/**
 * Columns that describe one laptop, never synced in either direction: `project.folder_path` is where this laptop
 * keeps the project folder (Field rewrites it on every open), so two laptops would overwrite each other's.
 */
export const DEVICE_LOCAL_COLUMNS: Readonly<Record<string, readonly string[]>> = {
  project: ['folder_path'],
};

/**
 * Columns only Teslyn Cloud sets: downloaded, never uploaded. `aircraft.cloud_photo_path` (decision 598) is set by
 * tenant admins, managers and office staff in Teslyn Cloud; a laptop whose base of the row predates the column
 * (migration 0043) would otherwise send it as changed and clear the photo, or have its other aircraft changes
 * refused. `project_photo.weekly_cover` (decisions 665 and 712, migration 0048) likewise: Teslyn Cloud refuses any
 * change of it but its own "Use for the weekly report cover".
 */
export const CLOUD_ONLY_WRITES: Readonly<Record<string, readonly string[]>> = {
  aircraft: ['cloud_photo_path'],
  project_photo: ['weekly_cover'],
  // R120: the sign-off requests are Teslyn Cloud's alone
  signoff_request: SIGNOFF_REQUEST_COLUMNS.filter((c) => c !== 'id'),
  // decision 889: the Geosoft template manifest is Teslyn Cloud's alone
  geosoft_template_file: GEOSOFT_TEMPLATE_FILE_COLUMNS.filter((c) => c !== 'id'),
  // decision 885: the final report's standard text is Teslyn Cloud's alone
  final_report_text: FINAL_REPORT_TEXT_COLUMNS.filter((c) => c !== 'id'),
};

/**
 * Columns the server stamps (updated_at by trigger, updated_by from the caller's JWT); never uploaded, always taken
 * from the cloud on download.
 */
export const SERVER_STAMPED_COLUMNS: readonly string[] = ['updated_at', 'updated_by'];

/**
 * Cloud tables the Field device also downloads (read only) so it can show and choose the organisation a project
 * set up offline belongs to. They are not stored in the project database.
 */
export const CLOUD_DIRECTORY_TABLES: readonly string[] = ['tenant', 'membership'];

/** The tenant id every local tenant-scoped row carries until a signed-in user links the project to a tenant. */
export const NIL_TENANT = '00000000-0000-0000-0000-000000000000';

export function syncedTable(name: string): SyncedTable {
  const t = SYNCED_TABLES.find((x) => x.name === name);
  if (!t) throw new Error(`Table ${name} is not synchronised.`);
  return t;
}

/** Postgres-only column added for the sync streams: history rows carry their project (20261018003100). */
export const STREAM_PROJECT_COLUMN: Readonly<Record<string, string>> = {
  line_status_history: 'project_id',
  cal_result_history: 'project_id',
};
